import React from "react";
import { useNavigate } from "react-router-dom";
import { useSearch } from "~/context/SearchContext";
import type { Masters } from "~/types";
import { createCategoryMap } from "~/utils/categoryProcessing";
import ServiceTypeSection from "./ServiceTypeSection";
import OccupationTypeSection from "./OccupationTypeSection";
import SearchAreaSection from "./SearchAreaSection";
import MediumButton from "~/components/MediumButton";
import SidebarListWrap from "./SidebarListWrap";
import { useSearchAreas } from "~/hooks";

type Props = {
  masters: Masters;
};

interface SearchButtonProps {
  onClick: () => void;
  isSearching: boolean;
}

const SearchButton: React.FC<SearchButtonProps> = ({
  onClick,
  isSearching,
}) => (
  <div className="mt-6 pb-4 [&_button]:w-full">
    <MediumButton
      text="再検索"
      icon="search"
      handler={onClick}
      disabled={isSearching}
    />
  </div>
);

const Sidebar: React.FC<Props> = ({ masters }) => {
  const [isSearching, setIsSearching] = React.useState(false);
  const navigate = useNavigate();
  const { conditions, setAppliedConditions, initialState } = useSearch();

  // Transform category data into more usable format
  const categoryMaps = {
    service: createCategoryMap(masters.serviceTypeCategories),
    occupation: createCategoryMap(masters.occupationTypeCategories),
    area: createCategoryMap(masters.searchAreaCategories),
  };

  const { isAreaSelected, generateAreaListPath } = 
    useSearchAreas(masters, categoryMaps.area, "tokyo");

  // Handle search action
  const handleSearchClick = () => {
    setIsSearching(true);
    setAppliedConditions(conditions);

    const hasConditions =
      JSON.stringify(conditions) !== JSON.stringify(initialState);

    const path = isAreaSelected(conditions) ? generateAreaListPath(conditions.search_area_ids) : "";
    const queryParams = hasConditions
      ? `?${new URLSearchParams(
          Object.entries(conditions)
            .filter(([_, value]) => value.length > 0)
            .reduce(
              (acc, [key, value]) => ({
                ...acc,
                [key]: value.join(","),
              }),
              {}
            )
        )}`
      : "";

    navigate(`/list${path}${queryParams}`);
    setIsSearching(false);
  };

  return (
    <div className="hidden md:grid md:grid-cols-1 md:gap-y-10">
      {/* Service Types Section */}
      <SidebarListWrap titleColor="secondary" titleText="業態" titleIcon="bowl">
        <ServiceTypeSection
          serviceTypes={masters.serviceTypes}
          serviceTypeCategories={categoryMaps.service}
        />
        <SearchButton onClick={handleSearchClick} isSearching={isSearching} />
      </SidebarListWrap>

      {/* Occupation Types Section */}
      <SidebarListWrap titleColor="primary" titleText="職種" titleIcon="shirt">
        <OccupationTypeSection
          occupationTypes={masters.occupationTypes}
          occupationTypeCategories={categoryMaps.occupation}
        />
        <SearchButton onClick={handleSearchClick} isSearching={isSearching} />
      </SidebarListWrap>

      {/* Search Areas Section */}
      <SidebarListWrap titleColor="tertiary" titleText="エリア" titleIcon="pin">
        <SearchAreaSection
          searchAreas={masters.searchAreas}
          searchAreaCategories={categoryMaps.area}
        />
        <SearchButton onClick={handleSearchClick} isSearching={isSearching} />
      </SidebarListWrap>
    </div>
  );
};

export default Sidebar;
